/**
 * FocusBoard — the triage's "where does the work go" card (Matt, 2026-10-25: "It's not so much about the
 * people as it is the projects"). The decision in the order a person makes it: a PROJECT (priority, recent
 * pressure, what is already in the air there), one of its ready milestones, then who flies it from the
 * people on no milestone. Nothing is proposed for a person. Pairs come together (tap one, the partner comes
 * too when free); anyone can be swapped. Built from api/alderflow.js's `focus:` card (`card.board`).
 * Guards: only people on no flight are offered, and a person placed once is gone from every other row.
 */
import { useState } from 'react'

const fmtDue = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null
const firstOf = (n) => String(n || '').split(/\s+/)[0]

export default function FocusBoard({ board, taken, onCommit, onSkip }) {
  const [picks, setPicks] = useState({})        // msId -> { ms, project, members: [person] }
  const [open, setOpen] = useState(null)        // msId whose crew picker is open
  const [showAll, setShowAll] = useState(false)
  const placed = new Set(Object.values(picks).flatMap(p => p.members.map(m => m.id)))
  const freeNow = (board.free || []).filter(f => !taken.has(f.id))
  const avail = (msId) => freeNow.filter(f => !placed.has(f.id) || (picks[msId] && picks[msId].members.some(m => m.id === f.id)))
  const byId = new Map((board.free || []).map(f => [f.id, f]))

  function toggle(project, ms, person) {
    setPicks(prev => {
      const cur = prev[ms.id] ? prev[ms.id].members : []
      let next
      if (cur.some(m => m.id === person.id)) next = cur.filter(m => m.id !== person.id)
      else {
        next = [...cur, person].slice(-2)
        // A pair comes together when the partner is free and the milestone has room.
        const mate = person.partnerId && byId.get(person.partnerId)
        if (mate && next.length < 2 && !next.some(m => m.id === mate.id) && freeNow.some(f => f.id === mate.id) && !placed.has(mate.id)) next = [...next, mate]
      }
      const out = { ...prev }
      if (next.length) out[ms.id] = { ms, project, members: next }
      else delete out[ms.id]
      return out
    })
  }

  const list = showAll ? board.projects : board.projects.slice(0, 6)
  const chosen = Object.values(picks)
  const loose = freeNow.filter(f => !placed.has(f.id))

  return (
    <div style={{ marginTop: 12 }}>
      <div style={S.freeLine}>
        <span style={S.lbl}>On no milestone</span>
        {freeNow.length ? freeNow.map(f => (
          <span key={f.id} style={{ ...S.chip, ...(placed.has(f.id) ? S.chipUsed : null) }}>{firstOf(f.name)}{f.partnerId && byId.get(f.partnerId) ? ' · pairs with ' + firstOf(byId.get(f.partnerId).name) : ''}{f.soloOn ? ' · solo on ' + f.soloOn : ''}</span>
        )) : <span style={S.muted}>Everyone has work after your earlier answers.</span>}
      </div>

      {list.map((p, pi) => (
        <div key={p.id} style={S.proj}>
          <div style={S.projHead}>
            <span style={S.projName}>{p.name.replace(/ Residence.*$/, '')}</span>
            <span style={{ ...S.muted, color: p.target ? (p.target.zone === 'red' ? '#b0413e' : p.target.zone === 'yellow' ? '#b7791f' : '#1f7a4d') : undefined }}>
              {p.target ? `Build Ready ${fmtDue(p.target.date)} · ${p.target.late ? 'late, no buffer left' : `${p.target.planDone || 0}% of the plan done · ${Math.round((p.target.burnPct || 0) * 100)}% of the buffer used`}` : p.behind ? `No Build Ready date yet · ${p.behind} working days behind` : 'No Build Ready date yet'}
            </span>
          </div>
          {(p.notes || []).map(n => <div key={n.id} style={S.note}>{n.by ? n.by + ' noted' : 'Noted'}: “{n.text}”</div>)}
          <div style={S.facts}>
            {p.inAir.length
              ? <span>In the air: {p.inAir.map(f => `${f.name}${f.crew.length ? ' (' + f.crew.join(', ') + ')' : ''}${f.holding ? ', holding' : ''}`).join('; ')}</span>
              : <span>Nothing in the air</span>}
            {p.pressure >= 1 && <span> · {p.pressure} hours logged lately with nobody crewed</span>}
          </div>
          {p.milestones.map(ms => {
            const pick = picks[ms.id]
            const isOpen = open === ms.id
            return (
              <div key={ms.id} style={{ ...S.ms, ...(pick ? S.msOn : null) }}>
                <button type="button" style={S.msBtn} onClick={() => setOpen(isOpen ? null : ms.id)}>
                  <span style={{ fontWeight: 500 }}>{ms.name}</span>
                  <span style={S.muted}>{[ms.kit ? 'kit clear' : 'kit still open', ms.due && 'due ' + fmtDue(ms.due), ms.kind !== 'design' && ms.kind + ' work'].filter(Boolean).join(' · ')}</span>
                  <span style={S.who}>{pick ? pick.members.map(m => firstOf(m.name)).join(' and ') : (isOpen ? '' : 'Crew it…')}</span>
                </button>
                {isOpen && (
                  <div style={S.pickRow}>
                    {avail(ms.id).length ? avail(ms.id).map(f => {
                      const on = pick && pick.members.some(m => m.id === f.id)
                      return <button key={f.id} type="button" onClick={() => toggle(p, ms, f)} style={{ ...S.pchip, ...(on ? S.pchipOn : null) }}>{firstOf(f.name)}</button>
                    }) : <span style={S.muted}>Nobody free; everyone is placed.</span>}
                    {pick && pick.members.length === 1 && !(pick.members[0].soloOn && pick.members[0].soloOn === ms.kind) && <span style={S.warn}>Flies alone.</span>}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      ))}
      {!showAll && board.projects.length > 6 && <button type="button" style={S.link} onClick={() => setShowAll(true)}>Show {board.projects.length - 6} more projects</button>}
      {board.more > 0 && showAll && <div style={S.muted}>{board.more} more projects have ready work further down the priority list.</div>}

      <div style={S.foot}>
        <div style={{ fontSize: 13, color: 'var(--aj-text-mid)', lineHeight: 1.5 }}>
          {chosen.length
            ? chosen.map(c => `${c.members.map(m => firstOf(m.name)).join(' and ')} onto ${c.project.name.replace(/ Residence.*$/, '')} ${c.ms.name}`).join('; ') + '.'
            : 'Nothing picked yet.'}
          {chosen.length > 0 && loose.length > 0 && <div style={S.muted}>Still on no milestone: {loose.map(f => firstOf(f.name)).join(', ')}.</div>}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
          <button type="button" style={S.ghost} onClick={onSkip}>Not today</button>
          <button type="button" style={{ ...S.go, ...(!chosen.length ? { opacity: 0.5 } : null) }} disabled={!chosen.length} onClick={() => onCommit(chosen)}>
            {chosen.length ? `Crew ${chosen.length} milestone${chosen.length === 1 ? '' : 's'}` : 'Pick a milestone'}
          </button>
        </div>
      </div>
    </div>
  )
}

const S = {
  lbl: { fontSize: 11, fontWeight: 600, color: 'var(--aj-text-muted)', marginRight: 4 },
  freeLine: { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 12 },
  chip: { fontSize: 12, padding: '3px 9px', borderRadius: 20, background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', color: 'var(--aj-text)' },
  chipUsed: { opacity: 0.4, textDecoration: 'line-through' },
  proj: { border: '0.5px solid var(--aj-border)', borderRadius: 10, padding: '10px 12px', marginBottom: 8, background: 'var(--aj-bg-surface)' },
  projHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 },
  projName: { fontSize: 15, fontWeight: 600, color: 'var(--aj-text)' },
  facts: { fontSize: 12, color: 'var(--aj-text-muted)', margin: '2px 0 6px', lineHeight: 1.45 },
  note: { fontSize: 12.5, color: '#b7791f', fontWeight: 600, margin: '3px 0 2px' },
  ms: { borderTop: '0.5px solid var(--aj-border)' },
  msOn: { background: 'var(--aj-bg-subtle)' },
  msBtn: { display: 'grid', gridTemplateColumns: '1fr auto', gap: '2px 10px', width: '100%', textAlign: 'left', background: 'none', border: 0, padding: '7px 2px', cursor: 'pointer', fontFamily: '"DM Sans", sans-serif', fontSize: 13.5, color: 'var(--aj-text)' },
  who: { gridColumn: 2, gridRow: '1 / 3', alignSelf: 'center', fontSize: 12.5, color: 'var(--aj-accent-blue, #2b6cb0)' },
  pickRow: { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', padding: '2px 2px 9px' },
  pchip: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, padding: '4px 11px', borderRadius: 20, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', cursor: 'pointer' },
  pchipOn: { background: '#07272D', color: '#fff', borderColor: '#07272D' },
  warn: { fontSize: 12, color: '#b7791f' },
  muted: { fontSize: 12, color: 'var(--aj-text-muted)' },
  link: { background: 'none', border: 0, padding: '4px 0', cursor: 'pointer', fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-accent-blue, #2b6cb0)' },
  foot: { borderTop: '0.5px solid var(--aj-border)', marginTop: 10, paddingTop: 10 },
  ghost: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, padding: '8px 14px', borderRadius: 6, border: '0.5px solid var(--aj-border-mid)', background: 'transparent', color: 'var(--aj-text-mid)', cursor: 'pointer' },
  go: { fontFamily: '"DM Sans", sans-serif', fontSize: 14, fontWeight: 600, padding: '9px 16px', borderRadius: 6, border: 0, background: '#07272D', color: '#fff', cursor: 'pointer' },
}
